import classNames from 'classnames';
import { type CSSProperties, type ReactNode, createElement } from 'react';
import GDPRCompliance from '../../signupElement/components/GDPRCompliance';
import type { BasicNode, DreamRenderElementProps, Price, StripeCardElementComponent, Tier } from '../../types';
import { giftFormHeading, giftFormLabel, input, inputSubmit } from '../Pricing.css';

interface Props {
  email?: string;
  onEmailChange: (value: string) => void;
  taxId: string;
  onTaxIdChange: (value: string) => void;
  ctaText: string;
  isCtaDisabled: boolean;
  showTaxIdField: boolean;
  StripeCardElement?: StripeCardElementComponent;
  selectedTier?: Tier;
  selectedPrice?: Price;
  PaymentRequestButtonElement?: ReactNode;
  hasTermsOfService?: boolean;
  hasCustomTermsOfService?: string;
  inputTextColor?: CSSProperties['color'];
  inputPlaceholderColor?: CSSProperties['color'];
  inputBackgroundColor?: CSSProperties['backgroundColor'];
  isInternalCheckout: boolean;
  translations?: DreamRenderElementProps<BasicNode>['translations'];
}

const GiftCheckoutForm = ({
  email,
  onEmailChange,
  taxId,
  onTaxIdChange,
  isCtaDisabled,
  StripeCardElement,
  selectedTier,
  selectedPrice,
  ctaText = 'Upgrade',
  showTaxIdField = false,
  PaymentRequestButtonElement,
  hasTermsOfService = false,
  hasCustomTermsOfService,
  inputTextColor,
  inputPlaceholderColor,
  inputBackgroundColor,
  isInternalCheckout,
  translations,
}: Props) => {
  if (!selectedTier || !selectedPrice) {
    return null;
  }

  const minDate = new Date().toISOString().split('T')[0];
  // The max date is 2 weeks from today
  const twoWeeks = new Date(new Date().getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const emailField = (
    <div className="flex flex-col gap-1">
      <label htmlFor="email" className={classNames(giftFormLabel)}>
        {translations?.['upgrade.tabs.gift.form.purchaser_email']}
      </label>
      <input
        name="email"
        defaultValue={email}
        onBlur={(e) => e.currentTarget.value && onEmailChange(e.currentTarget.value)}
        type="email"
        placeholder="example@example.com"
        className={classNames('border py-2 px-4 rounded-md', input)}
      />
    </div>
  );

  const purchaserNameField = (
    <div className="flex flex-col gap-1">
      <label htmlFor="purchaser_name" className={classNames(giftFormLabel)}>
        {translations?.['upgrade.tabs.gift.form.purchaser_name']}
      </label>
      <input
        name="purchaser_name"
        type="text"
        placeholder="John Doe"
        className={classNames('border py-2 px-4 rounded-md', input)}
      />
    </div>
  );

  const recipientEmailField = (
    <div className="flex flex-col gap-1">
      <label htmlFor="recipient_email" className={classNames(giftFormLabel)}>
        {translations?.['upgrade.tabs.gift.form.recipient_email']}
      </label>
      <input
        name="recipient_email"
        type="text"
        placeholder="example@example.com"
        className={classNames('border py-2 px-4 rounded-md', input)}
      />
    </div>
  );

  const messageField = (
    <div className="flex flex-col gap-1">
      <label htmlFor="message" className={classNames(giftFormLabel)}>
        {translations?.['upgrade.tabs.gift.form.message']}
      </label>
      <textarea
        name="message"
        placeholder={translations?.['upgrade.tabs.gift.form.message_placeholder']}
        className={classNames('border py-2 px-4 rounded-md', input)}
      />
    </div>
  );

  const startAtField = (
    <div className="flex flex-col gap-1">
      <label htmlFor="start_at" className={classNames(giftFormLabel)}>
        {translations?.['upgrade.tabs.gift.form.start_at']}
      </label>
      <input
        className={classNames('border py-2 px-4 rounded-md', input)}
        name="start_at"
        type="date"
        defaultValue={minDate}
        min={minDate}
        max={twoWeeks}
      />
    </div>
  );

  const gdprField = hasTermsOfService && (
    <GDPRCompliance hasCustomTermsOfService={hasCustomTermsOfService} defaultTOSConsentValue={true} />
  );

  const fields = (
    <>
      <h3 className={classNames(giftFormHeading)}>{translations?.['upgrade.tabs.gift.form.title']}</h3>
      {purchaserNameField}
      {emailField}
      {recipientEmailField}
      {messageField}
      {startAtField}
    </>
  );

  if (PaymentRequestButtonElement) {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {fields}
        {PaymentRequestButtonElement}
      </div>
    );
  }

  if (selectedPrice?.premium_offer && !selectedPrice?.premium_offer.card_required) {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {fields}
        {gdprField}
        <input
          disabled={isCtaDisabled}
          type="submit"
          value={ctaText}
          className={classNames(inputSubmit, 'rounded-md py-2 px-4')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 max-w-md w-full">
      {fields}
      {isInternalCheckout &&
        (StripeCardElement ? (
          createElement(StripeCardElement, {
            className: classNames('border py-3 px-4 rounded-md', input),
            options: {
              style: {
                base: {
                  backgroundColor: inputBackgroundColor,
                  fontSize: '14px',
                  color: inputTextColor,
                  '::placeholder': {
                    color: inputPlaceholderColor,
                  },
                },
              },
            },
          })
        ) : (
          <input
            type="text"
            placeholder="Credit Card Placeholder"
            className={classNames('border py-2 px-4 rounded-md', input)}
          />
        ))}
      {showTaxIdField && (
        <input
          type="text"
          name="tax_id"
          placeholder="Tax ID"
          defaultValue={taxId}
          onBlur={(e) => onTaxIdChange(e.target.value)}
          className={classNames('border py-2 px-4 rounded-md', input)}
        />
      )}
      <input
        disabled={isCtaDisabled}
        type="submit"
        value={ctaText}
        className={classNames(inputSubmit, 'rounded-md py-2 px-4')}
      />
    </div>
  );
};

export default GiftCheckoutForm;
